import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { PageContainer, EntityHeader, IsMassAccount, AccessControl, AuditLogsComponent, HasAccess } from 'modules';
import { VISIT_ROLE_RULE_SET, visitPermissionData } from 'modules/EntityHeader/visitEditAccess';
import { Button, Loader, TabOrganizer } from 'ui-kit';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { SubNav, SubNavMobile, useNavItemAccess } from 'modules/SubNav';
import * as Scaffold from 'modules/ListScaffold';
import { useBreakpoint, useUrlParamState } from 'hooks';
import AuditLogsTableNew from 'modules/AuditLogsTable/AuditLogsTableNew';
import useNewUi from 'hooks/useNewUi';
import { VisitEntity } from '_entities';
import PropTypes from 'prop-types';

import { BUTTON_TYPES, PERMISSIONS, URLS, USER_ROLE_TYPES } from '_constants';
import { SECTION_GROUPS, DEFAULT_SECTION, ALL_SECTION_KEYS, findSectionMeta } from './sections.config';
import { PAGE_TYPE } from './constants';
import VisitViewDetails from './components/VisitViewDetails';
import VisitNotes from './components/VisitNotes';
import * as Styled from './styles';

const { getVisitInfo, setVisitInfo, resetVisitData, resetViolationData, getViolationInfo } = VisitEntity.actions;
const { getVisitData } = VisitEntity.selectors;

function VisitView(props) {
  const dispatch = useDispatch();
  const [isInspectorMode, setIsInspectorMode] = useState(false);
  // Mirrored into `?activeTab=` like the retailer view and the list pages, so a refresh keeps the reader
  // on the same section.
  const [activeTab, setActiveTab] = useUrlParamState('activeTab', DEFAULT_SECTION, ALL_SECTION_KEYS);
  const history = useHistory();
  const { id } = useParams();
  const newUi = useNewUi();
  const breakpoint = useBreakpoint();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { data: visitData } = useSelector(getVisitData);
  const { viewUrl, notesResource, editPageUrl } = props;

  const [isLoading, setIsLoading] = useState(true);
  const isMassAccount = IsMassAccount();
  const visitAttributes = visitData && visitData.attributes;
  // The Edit button's own rule (visitEditAccess): a note's edit pencil leads to the same page, so it
  // shows for exactly the users who see that button — and never on a deleted visit.
  const canEditVisit =
    HasAccess(PERMISSIONS.CHANGE_VISIT, visitPermissionData({ data: visitData }), VISIT_ROLE_RULE_SET) &&
    !visitAttributes?.deleted;
  const hasViolationInfo = visitAttributes && visitAttributes?.is_violation_open_status !== null;
  const { MULTI_ACCOUNT_MANAGER, ACCOUNT_MANAGER } = USER_ROLE_TYPES;

  // Role permission AND account module, the same predicate SubNav filters with — so the section list
  // here can never disagree with the one the sidebar renders.
  const canSeeSection = useNavItemAccess();
  const visibleSections = useMemo(
    () => SECTION_GROUPS.flatMap((group) => group.items).filter(canSeeSection),
    [canSeeSection],
  );
  // The switcher (sidebar or tab strip) only earns its place once there is something to switch to.
  const hasSectionSwitcher = visibleSections.length > 1;

  const tabsTitle = useMemo(() => visibleSections.map((item) => item.label), [visibleSections]);

  // A section can disappear under the user — the module is toggled off, or a deep link names one they
  // cannot see — so fall back to the details rather than render an empty content area.
  useEffect(() => {
    if (visibleSections.length && !visibleSections.some((item) => item.key === activeTab)) {
      setActiveTab(DEFAULT_SECTION);
    }
  }, [visibleSections, activeTab, setActiveTab]);

  const updateVisitInfo = useCallback(async () => {
    try {
      setIsLoading(true);
      const payload = { ...props };
      await Promise.all([dispatch(getVisitInfo(id)), dispatch(setVisitInfo(payload))]);
    } catch (err) {
      history.push(URLS.notFound);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateVisitInfo();
    return async () => {
      // Prevent show data from previous visit/violation after failed request
      await Promise.all([dispatch(resetVisitData()), dispatch(resetViolationData())]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isMassAccount || !hasViolationInfo) return;
    (async () => {
      setIsLoading(true);
      try {
        await dispatch(getViolationInfo(id));
      } catch (err) {
        // Non-fatal: loading state cleared in finally; violation tab renders empty.
      } finally {
        setIsLoading(false);
      }
    })();
  }, [hasViolationInfo, dispatch, id, isMassAccount]);

  const renderTabContent = useCallback(() => {
    switch (activeTab) {
      case 'Visit details':
        return <VisitViewDetails isInspectorMode={isInspectorMode} />;
      case 'Notes':
        return (
          <VisitNotes
            visitId={id}
            resource={notesResource}
            editUrl={canEditVisit && editPageUrl ? `${editPageUrl}/${id}` : null}
          />
        );
      case 'Audit logs':
        return (
          <AuditLogsComponent
            objectType="visit"
            recordId={id}
            showSearch
            tableComponent={newUi ? AuditLogsTableNew : undefined}
            searchInToolbar={newUi}
          />
        );
      default:
        return <VisitViewDetails isInspectorMode={isInspectorMode} />;
    }
  }, [activeTab, isInspectorMode, id, newUi, notesResource, canEditVisit, editPageUrl]);

  const activeMeta = useMemo(() => findSectionMeta(activeTab), [activeTab]);
  const ActiveIcon = activeMeta?.icon;

  if (isLoading) return <Loader />;

  return (
    <PageContainer>
      <Styled.Wrapper>
        <EntityHeader
          isInspectorMode={isInspectorMode}
          pageType={PAGE_TYPE.VIEW}
          viewUrl={viewUrl}
          updateVisitInfo={updateVisitInfo}
        />
        <AccessControl roleRuleSet={{ all: [ACCOUNT_MANAGER, MULTI_ACCOUNT_MANAGER] }}>
          <Styled.InspectorModeButtonWrapper>
            <Button
              onClick={() => setIsInspectorMode((prev) => !prev)}
              text={isInspectorMode ? 'Deactivate inspector mode' : 'Activate inspector mode'}
              variant={isInspectorMode ? BUTTON_TYPES.DANGER : BUTTON_TYPES.DARK}
            />
          </Styled.InspectorModeButtonWrapper>
        </AccessControl>
        {newUi && hasSectionSwitcher ? (
          /* New design: the tabs become a sidebar, matching the retailer, campaign and venue views.
             Only rendered when there is more than one section to switch between — with neither the
             audit-log permission nor the notes module the details fill the page, just as the tab
             strip was hidden. */
          <Scaffold.Layout collapsed={breakpoint === 'desktop' && sidebarCollapsed}>
            {breakpoint === 'mobile' ? (
              <SubNavMobile groups={SECTION_GROUPS} activeKey={activeTab} onSelect={setActiveTab} />
            ) : (
              <Scaffold.SidebarColumn>
                {breakpoint === 'desktop' && (
                  <Scaffold.SidebarHeader collapsed={sidebarCollapsed}>
                    <Scaffold.CollapseToggle
                      type="button"
                      onClick={() => setSidebarCollapsed((c) => !c)}
                      title={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
                      aria-label={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
                    >
                      {sidebarCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
                    </Scaffold.CollapseToggle>
                  </Scaffold.SidebarHeader>
                )}
                <SubNav
                  groups={SECTION_GROUPS}
                  activeKey={activeTab}
                  onSelect={setActiveTab}
                  collapsed={sidebarCollapsed}
                />
              </Scaffold.SidebarColumn>
            )}
            <Scaffold.ContentArea>
              <Styled.SectionHeading>
                {ActiveIcon && <ActiveIcon size={20} />}
                <h2>{activeMeta?.label}</h2>
              </Styled.SectionHeading>
              {renderTabContent()}
            </Scaffold.ContentArea>
          </Scaffold.Layout>
        ) : (
          <>
            {hasSectionSwitcher && (
              <TabOrganizer
                tabsTitle={tabsTitle}
                useActiveTab={setActiveTab}
                selectedValue={activeTab}
                minWidth="100%"
                isView
                isBorder
                minHeight="42px"
                isMargin="0 0 16px 0"
                inputId="VisitViewTabs"
              />
            )}
            {renderTabContent()}
          </>
        )}
      </Styled.Wrapper>
    </PageContainer>
  );
}

VisitView.propTypes = {
  viewUrl: PropTypes.string,
  // The visit-type API resource behind this page, for the Notes section's endpoint.
  notesResource: PropTypes.string,
  // This visit type's Edit Visit route, for the Notes section's edit pencils.
  editPageUrl: PropTypes.string,
};

VisitView.defaultProps = {
  viewUrl: '',
  notesResource: '',
  editPageUrl: '',
};

export default VisitView;
